import React, { useCallback, useMemo } from "react";
import { useHistory, useLocation, matchPath } from "react-router-dom";
import Box from "@mui/material/Box";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import Drawer from "./Drawer";
import DrawerHeader from "./DrawerHeader";
import MenuNavList from "./MenuNavList";
// import MenuNavSetting from "./MenuNavSetting";
import TopLogo from "./TopLogo";
import BottomLogo from "./BottomLogo";
import useBoundStore from "../../store";
import { useMe } from "../../app/pages/auth/LoginAuth";
import {
  ScoutReportIcon,
  SiderMyDarsboardIcon,
  SiderIntelligenceIcon,
  SiderAttackPathIcon,
  SiderAssetToolsIcon,
  SiderIdentityToolsIcon,
  SiderTicketManagementIcon,
  SiderDocumentsIcon,
  SiderSynapseEngineIcon,
  SiderSourceManagementIcon,
  SiderUserManagementIcon,
  SiderPredictiveAnalyticsIcon,
  SiderReportIcon,
  SiderPathFinderIcon,
  SiderImpactMatrixIcon,
  SiderVulnerabilitiesIcon,
  SiderRogueReportIcon,
  SiderInventoryIcon,
  SiderActiveDirectory,
  SiderTicketsIcon,
  SiderShareIcon,
  SiderAgentControls,
  SiderUserAdministrationIcon,
  SiderRolesAdministrationIcon,
  SiderGroupsAdministrationIcon,
  SiderConfigurationIcon,
  SiderExposureIcon,
  SiderImpactIcon, SiderCaseManagementIcon, SiderCaseReportIcon,
} from "../../components-v2/icons";
import { TListItem } from "../../components-v2/MenuList";
import MsspSelector from "./MsspSelector";
import routes from "../../config/page-route";
import layoutConfig from "../layout-config.json";

// This needs API changes.
const menuIcons = {
  "My Dashboards": <SiderMyDarsboardIcon />,
  Intelligence: <SiderIntelligenceIcon />,
  "Predictive Anatytics": <SiderPredictiveAnalyticsIcon />,
  "Early Warnings": <SiderPredictiveAnalyticsIcon />,
  Dashboards: <SiderMyDarsboardIcon />,
  Reports: <SiderReportIcon />,
  "Attack Path Tools": <SiderAttackPathIcon />,
  "Path Finder": <SiderPathFinderIcon />,
  "Impact Matrix": <SiderImpactMatrixIcon />,
  Vulnerabilities: <SiderVulnerabilitiesIcon />,
  "Asset Tools": <SiderAssetToolsIcon />,
  "Rogue Report": <SiderRogueReportIcon />,
  Inventory: <SiderInventoryIcon />,
  "Identity Tools": <SiderIdentityToolsIcon />,
  "Active Directory": <SiderActiveDirectory />,
  "Tickets Management": <SiderTicketManagementIcon />,
  "Case Management": <SiderCaseManagementIcon />,
  "Case Report": <SiderCaseReportIcon/>,
  Tickets: <SiderTicketsIcon />,
  Documents: <SiderDocumentsIcon />,
  Shared: <SiderShareIcon />,
  "Synapse Engine": <SiderSynapseEngineIcon />,
  "Agent Controls": <SiderAgentControls />,
  "User Management": <SiderUserManagementIcon />,
  "User Administration": <SiderUserAdministrationIcon />,
  "Roles Administration": <SiderRolesAdministrationIcon />,
  "Groups Administration": <SiderGroupsAdministrationIcon />,
  "Source Management": <SiderSourceManagementIcon />,
  Configuration: <SiderConfigurationIcon />,
  "Scout Engine": (
    <i className="fa fa-border-none fa-2x" style={{ color: "#3E4784" }} />
  ),
  "Scout Reports": <ScoutReportIcon />,
  Exposure: <SiderExposureIcon />,
  Impact: <SiderImpactIcon />,
};

const convertMenuItems = (items: any): TListItem[] => {
  if (!items?.length) return [];
  return items.map((item) => {
    const newItem: TListItem = {
      label: item.title || "Untitled",
      value:
        item.title === "Key Performance Metrics"
          ? `intelligence-static/${item.page}`
          : item.title === "Security Operations Health"
          ? "case-report"
          : item.page || item.title,
    };

    const icon = menuIcons[item.title];
    if (icon) {
      newItem.icon = icon;
    } else if (item.icon) {
      // Check if the icon class contains 'menu-small-fa' and adjust size accordingly
      const isSmall = item.icon.includes('menu-small-fa');
      const sizeClass = isSmall ? 'fa-lg' : 'fa-2x';
      newItem.icon = <i className={`${item.icon} ${sizeClass}`} style={{ color: "#3E4784" }} />;
    }

    if (item.badge) {
      newItem.badge = item.badge;
    }

    if (item.submenu?.length) {
      newItem.children =
        item.title === "Scout Reports"
          ? convertMenuItems(item.submenu).map((sb) => ({
              ...sb,
              value: `scout-report/${sb.value}`,
            }))
          : convertMenuItems(item.submenu);
      
      // Add icons to subitems if they don't have one
      newItem.children = newItem.children.map(child => {
        if (!child.icon) {
          if (child.label === "Service Requests") {
             child.icon = <SiderTicketsIcon />;
          } else if (child.label === "Cases") {
             child.icon = <SiderCaseManagementIcon />;
          } else if (newItem.label === "Security Operations Health") {
             child.icon = <SiderCaseReportIcon />;
          }
        }
        return child;
      });
    }
    return newItem;
  });
};

const findMatchedMenuItem = (items: any, pathname: string) => {
  for (const item of items) {
    if (pathname.includes(`/${item.value}`)) return item;
    if (item.children?.length) {
      const result = findMatchedMenuItem(item.children, pathname);
      if (result) return result;
    }
  }
  return null;
};

const Sider = () => {
  const { updateConfig, config } = useBoundStore((state) => state);
  const me: any = useMe();
  const history = useHistory();
  const location = useLocation();

  const dynamicMenus = useMemo(() => {
    if (!me.menuConfig) return [];
    let asideMenus = convertMenuItems(me.menuConfig.aside?.items);
    const adminMenus = convertMenuItems(me.menuConfig.admin?.items);

    if (!me.systemOptions?.hasSwimlane) {
      asideMenus = asideMenus.filter(
        (item) =>
          item.label !== "Cases" && item.label !== "Security Operations Health"
      );
    }

    const menus = [...asideMenus, ...adminMenus];
    return menus;
  }, [me.menuConfig, me.systemOptions]);

  const currentMenuItem = useMemo(() => {
    const item = findMatchedMenuItem(dynamicMenus, location.pathname);
    if (!item && !!history.location.state?.prevPath) {
      return findMatchedMenuItem(dynamicMenus, history.location.state.prevPath);
    }
    return item;
  }, [location.pathname, history.location.state, dynamicMenus]);

  const onDrawerExpand = useCallback(() => {
    updateConfig();
  }, [updateConfig]);

  const onNavItemClick = useCallback(
    (navItem: TListItem) => {
      if (
        routes.find((route) =>
          matchPath(`/${navItem.value}`, {
            path: route.path,
            exact: true,
            strict: false,
          })
        )
      ) {
        history.push(`/${navItem.value}`);
      }
    },
    [history]
  );

  return (
    <Drawer variant="permanent" open={config.siderExpanded}>
      <DrawerHeader expanded={config.siderExpanded}>
        <TopLogo expanded={config.siderExpanded} />
      </DrawerHeader>
      {me.systemOptions?.enableMssp && config.siderExpanded && (
        <MsspSelector
          currentRealmId={me.realm}
          rawAgencies={me.systemOptions?.msspAgencies}
          rawMsspMasterRealm={me.systemOptions?.msspMasterRealm}
          onSwitchTo={(realmId, masterRealm) => {
            me.switchRealm(realmId, masterRealm);
          }}
        />
      )}
      <Box
        sx={{
          height: `calc(100% - ${
            (config.siderExpanded
              ? layoutConfig.extendedLogoHeight
              : layoutConfig.topNavbarHeight) -
            (me.systemOptions?.isMsspMaster && config.siderExpanded
              ? layoutConfig.msspSectionHeight
              : 0)
          }px)`,
          display: "flex",
          flexDirection: "column",
          bgcolor: "primary.main",
        }}
      >
        {/* {config.siderExpanded && (
          <XInput
            placeholder="Search"
            sxProps={{ m: 2, bgcolor: "primary.light", border: "none" }}
            startAdornment={<SearchIcon />}
            value={menuText}
            onChange={(e) => setMenuText(e.target.value)}
            clearable
            onClear={() => setMenuText("")}
          />
        )} */}
        <Box
          sx={{
            display: "flex",
            flexGrow: 1,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <MenuNavList
            siderExpanded={config.siderExpanded}
            list={dynamicMenus}
            selectedItem={currentMenuItem || dynamicMenus[0]}
            onSelectedItem={onNavItemClick}
          />
        </Box>
        {/* <MenuNavSetting
          siderExpanded={config.siderExpanded}
          list={navSettings}
          selectedItem={{ label: "Foo", value: "Foo" }}
          onSelectedItem={() => {}}
        /> */}
        <BottomLogo expanded={config.siderExpanded} />
      </Box>
      <Box
        sx={{
          position: "fixed",
          top: "53%",
          transform: "translate(35%, -50%)",
          left: config.siderExpanded
            ? layoutConfig.drawerWidth
            : layoutConfig.condensedDrawerWidth,
          width: 14,
          height: 148,
          borderRadius: 4,
          bgcolor: "grey.600",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          ":hover": {
            bgcolor: "grey.500",
          },
        }}
        onClick={onDrawerExpand}
      >
        {config.siderExpanded ? (
          <KeyboardArrowLeft sx={{ color: "primary.light", fontSize: 28 }} />
        ) : (
          <KeyboardArrowRight sx={{ color: "primary.light", fontSize: 28 }} />
        )}
      </Box>
    </Drawer>
  );
};

export default Sider;